import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Chip,
  Tooltip as MuiTooltip,
  Snackbar,
} from '@mui/material';
import { Edit, RocketLaunch, Merge, Close } from '@mui/icons-material';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import {
  AdminFeatureRequest,
  AdminFeatureRequestListResponse,
  DuplicateCandidate,
  FEATURE_REQUEST_CATEGORIES,
  FeatureRequestStatus,
  ImpactRow,
  STATUS_LABELS,
} from '../types/featureRequests';
import StatusPill from '../components/featureRequests/StatusPill';
import CategoryChip from '../components/featureRequests/CategoryChip';
import AdminRequestEditDialog from '../components/featureRequests/AdminRequestEditDialog';
import ShipDialog from '../components/featureRequests/ShipDialog';
import { colors } from '../theme/theme';

type AdminTab = 'triage' | 'duplicates' | 'impact';

const ImpactTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const row: ImpactRow = payload[0].payload;
  return (
    <Box sx={{ p: 1.25, backgroundColor: colors.background, border: `1px solid ${colors.divider}`, borderRadius: 1.5, boxShadow: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.title}</Typography>
      <Typography variant="caption" color="text.secondary">
        {row.voteCount} votes · effort {row.effortEstimate}
      </Typography>
    </Box>
  );
};

const AdminFeatureRequestsPage: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>('triage');

  // Triage
  const [items, setItems] = useState<AdminFeatureRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminFeatureRequest | null>(null);
  const [shipping, setShipping] = useState<AdminFeatureRequest | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  // Duplicates
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[]>([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(true);
  const [duplicatesError, setDuplicatesError] = useState<string | null>(null);
  const [keepChoice, setKeepChoice] = useState<Record<string, 'A' | 'B'>>({});
  const [resolving, setResolving] = useState<string | null>(null);

  // Impact
  const [impactRows, setImpactRows] = useState<ImpactRow[]>([]);
  const [impactLoading, setImpactLoading] = useState(true);
  const [impactError, setImpactError] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchTriage = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { page: targetPage };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const response = await apiService.get<AdminFeatureRequestListResponse>(
        API_ENDPOINTS.ADMIN_FEATURE_REQUESTS.LIST,
        { params },
      );
      setItems(response.items);
      setTotal(response.total);
      setPage(targetPage);
    } catch (err: any) {
      setError(err?.message || 'Could not load feature requests.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  const fetchDuplicates = useCallback(async () => {
    setDuplicatesLoading(true);
    setDuplicatesError(null);
    try {
      const data = await apiService.get<DuplicateCandidate[]>(API_ENDPOINTS.ADMIN_FEATURE_REQUESTS.DUPLICATES);
      setDuplicates(data);
    } catch (err: any) {
      setDuplicatesError(err?.message || 'Could not load duplicate candidates.');
    } finally {
      setDuplicatesLoading(false);
    }
  }, []);

  const fetchImpact = useCallback(async () => {
    setImpactLoading(true);
    setImpactError(null);
    try {
      const data = await apiService.get<ImpactRow[]>(API_ENDPOINTS.ADMIN_FEATURE_REQUESTS.IMPACT);
      setImpactRows(data);
    } catch (err: any) {
      setImpactError(err?.message || 'Could not load impact data.');
    } finally {
      setImpactLoading(false);
    }
  }, []);

  useEffect(() => { fetchTriage(1); }, [fetchTriage]);
  useEffect(() => { if (tab === 'duplicates') fetchDuplicates(); }, [tab, fetchDuplicates]);
  useEffect(() => { if (tab === 'impact') fetchImpact(); }, [tab, fetchImpact]);

  useEffect(() => {
    if (tab === 'triage' && highlightId && rowRefs.current[highlightId]) {
      rowRefs.current[highlightId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const t = setTimeout(() => setHighlightId(null), 2200);
      return () => clearTimeout(t);
    }
  }, [tab, highlightId, items]);

  const handleSaveEdit = async (
    id: string,
    patch: Partial<{ category: string; status: FeatureRequestStatus; visibility: 'public' | 'private'; adminNotes: string; effortEstimate: number | null }>,
  ) => {
    try {
      await apiService.patch(API_ENDPOINTS.ADMIN_FEATURE_REQUESTS.UPDATE(id), patch);
      setSnackbar({ message: 'Saved.', severity: 'success' });
      fetchTriage(page);
    } catch (err: any) {
      setSnackbar({ message: err?.message || 'Could not save changes.', severity: 'error' });
    }
  };

  const handleShip = async (releaseNote: string, isPublic: boolean) => {
    if (!shipping) return;
    try {
      await apiService.post(API_ENDPOINTS.ADMIN_FEATURE_REQUESTS.SHIP(shipping._id), { releaseNote, public: isPublic });
      setSnackbar({ message: 'Shipped! Voters and the author have been notified.', severity: 'success' });
      fetchTriage(page);
    } catch (err: any) {
      setSnackbar({ message: err?.message || 'Could not ship this request.', severity: 'error' });
    }
  };

  const handleMerge = async (candidate: DuplicateCandidate) => {
    const choice = keepChoice[candidate._id];
    if (!choice) return;
    setResolving(candidate._id);
    try {
      const keepId = choice === 'A' ? candidate.requestA._id : candidate.requestB._id;
      await apiService.post(API_ENDPOINTS.ADMIN_FEATURE_REQUESTS.DUPLICATE_MERGE(candidate._id), { keep: keepId });
      setDuplicates((prev) => prev.filter((c) => c._id !== candidate._id));
      setSnackbar({ message: 'Merged.', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ message: err?.message || 'Could not merge these requests.', severity: 'error' });
    } finally {
      setResolving(null);
    }
  };

  const handleDismiss = async (candidate: DuplicateCandidate) => {
    setResolving(candidate._id);
    try {
      await apiService.post(API_ENDPOINTS.ADMIN_FEATURE_REQUESTS.DUPLICATE_DISMISS(candidate._id));
      setDuplicates((prev) => prev.filter((c) => c._id !== candidate._id));
    } catch (err: any) {
      setSnackbar({ message: err?.message || 'Could not dismiss this candidate.', severity: 'error' });
    } finally {
      setResolving(null);
    }
  };

  const plottableImpact = impactRows.filter((r) => r.effortEstimate != null);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Feature Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Triage what studios are asking for, resolve duplicates, and see what to build next.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
      >
        <Tab value="triage" label="Triage Queue" />
        <Tab value="duplicates" label={`Duplicate Review${duplicates.length ? ` (${duplicates.length})` : ''}`} />
        <Tab value="impact" label="Impact vs Effort" />
      </Tabs>

      {tab === 'triage' && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All statuses</MenuItem>
              {(Object.keys(STATUS_LABELS) as FeatureRequestStatus[]).map((s) => (
                <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All categories</MenuItem>
              {FEATURE_REQUEST_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : items.length === 0 ? (
            <Alert severity="info">No requests match this filter.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Visibility</TableCell>
                    <TableCell align="right">Votes</TableCell>
                    <TableCell align="right">Effort</TableCell>
                    <TableCell>Studio</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item._id}
                      ref={(el) => { rowRefs.current[item._id] = el; }}
                      sx={{
                        transition: 'background-color 600ms ease',
                        backgroundColor: highlightId === item._id ? `${colors.primary}22` : 'transparent',
                      }}
                    >
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{item.title}</Typography>
                      </TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell><StatusPill status={item.status} /></TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.visibility === 'private' ? 'Private' : 'Public'}
                          color={item.visibility === 'private' ? 'secondary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{item.voteCount}</TableCell>
                      <TableCell align="right">{item.effortEstimate ?? '—'}</TableCell>
                      <TableCell>{item.submittedByStudio?.studioName || '—'}</TableCell>
                      <TableCell align="right">
                        <MuiTooltip title="Edit">
                          <IconButton size="small" onClick={() => setEditing(item)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </MuiTooltip>
                        {item.status !== 'shipped' && (
                          <MuiTooltip title="Ship">
                            <IconButton size="small" onClick={() => setShipping(item)}>
                              <RocketLaunch fontSize="small" />
                            </IconButton>
                          </MuiTooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {items.length < total && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button onClick={() => fetchTriage(page + 1)}>Load more</Button>
            </Box>
          )}
        </Box>
      )}

      {tab === 'duplicates' && (
        <Box>
          {duplicatesError && <Alert severity="error" sx={{ mb: 2 }}>{duplicatesError}</Alert>}
          {duplicatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : duplicates.length === 0 ? (
            <Alert severity="info">No pending duplicates to review.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {duplicates.map((candidate) => (
                <Card key={candidate._id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Chip size="small" color="secondary" label={`${Math.round(candidate.similarityScore * 100)}% similar`} />
                    </Box>
                    <RadioGroup
                      value={keepChoice[candidate._id] || ''}
                      onChange={(e) => setKeepChoice((prev) => ({ ...prev, [candidate._id]: e.target.value as 'A' | 'B' }))}
                    >
                      <Grid container spacing={2}>
                        {([['A', candidate.requestA], ['B', candidate.requestB]] as const).map(([key, req]) => (
                          <Grid key={key} size={{ xs: 12, sm: 6 }}>
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1.5px solid ${keepChoice[candidate._id] === key ? colors.primary : colors.divider}`,
                                height: '100%',
                              }}
                            >
                              <FormControlLabel
                                value={key}
                                control={<Radio size="small" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Keep this one</Typography>}
                              />
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5 }}>{req.title}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                                {req.description}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <StatusPill status={req.status} />
                                <CategoryChip category={req.category} />
                                <Chip size="small" variant="outlined" label={`${req.voteCount} votes`} />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Submitted by {req.submittedByStudio?.studioName || 'a studio'}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                      <Button
                        color="inherit"
                        startIcon={<Close />}
                        onClick={() => handleDismiss(candidate)}
                        disabled={resolving === candidate._id}
                      >
                        Dismiss
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Merge />}
                        onClick={() => handleMerge(candidate)}
                        disabled={!keepChoice[candidate._id] || resolving === candidate._id}
                      >
                        Merge
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      {tab === 'impact' && (
        <Box>
          {impactError && <Alert severity="error" sx={{ mb: 2 }}>{impactError}</Alert>}
          {impactLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : plottableImpact.length === 0 ? (
            <Alert severity="info">
              No requests have an effort estimate yet. Set one from the Triage tab to see it plotted here.
            </Alert>
          ) : (
            <ResponsiveContainer width="100%" height={440}>
              <ScatterChart margin={{ top: 20, right: 30, bottom: 30, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.divider} />
                <XAxis type="number" dataKey="effortEstimate" name="Effort" label={{ value: 'Effort estimate', position: 'insideBottom', offset: -15 }} />
                <YAxis type="number" dataKey="voteCount" name="Votes" label={{ value: 'Votes', angle: -90, position: 'insideLeft' }} />
                <RechartsTooltip content={<ImpactTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                <Scatter
                  data={plottableImpact}
                  fill={colors.primary}
                  cursor="pointer"
                  onClick={(point: any) => {
                    setHighlightId(point._id);
                    setTab('triage');
                  }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </Box>
      )}

      <AdminRequestEditDialog request={editing} onClose={() => setEditing(null)} onSave={handleSaveEdit} />
      <ShipDialog request={shipping} onClose={() => setShipping(null)} onConfirm={handleShip} />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>{snackbar.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default AdminFeatureRequestsPage;
